// ============================================================
// frontend/src/views/HomeView.tsx
// ============================================================
// Dashboard ("Today" tab). Loads /api/sessions/dashboard and shows:
//   - Stat cards (active clients, today's sessions, pending payments)
//   - Today's sessions list
//   - Upcoming (next 5)
//   - Payment-pending packages
// ============================================================

import { useEffect, useState } from 'react';
import { ChevronRight, HelpCircle } from 'lucide-react';
import { api } from '../lib/api';
import { formatTime, formatDateTime } from '../lib/format';
import { Section, Stat, StatusPill } from '../components/UI';
import { ConsiliumLoader } from '../components/ConsiliumLoader';
import type { DashboardResponse } from '@consilium/shared';
import type { ViewState } from '../App';

interface HomeViewProps {
  setView: (v: ViewState) => void;
  showError: (msg: string) => void;
}

export function HomeView({ setView, showError }: HomeViewProps) {
  const [data, setData] = useState<DashboardResponse | null>(null);

  // Load on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get<DashboardResponse>('/sessions/dashboard');
        if (!cancelled) setData(d);
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load dashboard');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Loading state — minimal skeleton so layout doesn't jump.
  if (!data) {
    return (
      <div>
        <div className="mono-cap">Loading</div>
        <h1 className="page-title">Today's practice</h1>
        <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ConsiliumLoader size={64} />
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 28 }}>
        <div className="mono-cap">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </div>
        <h1 className="page-title">Today's practice</h1>
      </div>

      {data.stats.activeClients === 0 && (
        <button
          onClick={() => setView({ name: 'guide' })}
          className="card"
          style={{
            width: '100%', textAlign: 'left', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 14,
            padding: 18, marginBottom: 28,
            border: '1px solid var(--accent)', background: 'var(--accent-glow)',
          }}
        >
          <HelpCircle size={22} color="var(--accent)" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>New to Consilium?</div>
            <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 2 }}>
              Read the 5-step guide to see how clients, packages, sessions, and payments fit together.
            </div>
          </div>
          <ChevronRight size={18} color="var(--muted-strong)" />
        </button>
      )}

      <div className="stats-grid" style={{ marginBottom: 28 }}>
        <Stat label="Active clients" value={data.stats.activeClients} />
        <Stat label="Sessions today" value={data.today.length} />
        <Stat label="Pending" value={data.paymentDue.length} />
      </div>

      <Section title="Today's sessions" count={data.today.length}>
        {data.today.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--muted-strong)', fontSize: 14, textAlign: 'center' }}>
            Nothing scheduled today.
          </div>
        ) : data.today.map((s) => (
          <div
            key={s.id} className="list-row"
            onClick={() => setView({ name: 'session', sessionId: s.id })}
          >
            <div className="row-main">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span className="display" style={{ fontSize: 16, color: 'var(--accent)', fontWeight: 600 }}>
                  {formatTime(s.scheduledAt)}
                </span>
                <span className="row-primary" style={{ display: 'inline' }}>{s.clientName}</span>
              </div>
              {s.agenda && <div className="row-secondary">{s.agenda}</div>}
            </div>
            <div className="row-meta">
              <StatusPill status={s.status} />
              <ChevronRight size={18} color="var(--muted-strong)" />
            </div>
          </div>
        ))}
      </Section>

      <Section title="Upcoming" count={data.upcoming.length}>
        {data.upcoming.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--muted-strong)', fontSize: 14, textAlign: 'center' }}>
            No upcoming sessions.
          </div>
        ) : data.upcoming.map((s) => (
          <div
            key={s.id} className="list-row"
            onClick={() => setView({ name: 'session', sessionId: s.id })}
          >
            <div className="row-main">
              <div className="row-primary">{s.clientName}</div>
              <div className="row-secondary">{formatDateTime(s.scheduledAt)}</div>
            </div>
            <ChevronRight size={18} color="var(--muted-strong)" />
          </div>
        ))}
      </Section>

      {data.paymentDue.length > 0 && (
        <Section title="Payment pending" count={data.paymentDue.length}>
          {data.paymentDue.map((pkg) => {
            const remaining = pkg.totalFee - pkg.paidTotal;
            return (
              <div
                key={pkg.id} className="list-row"
                onClick={() => setView({ name: 'client', clientId: pkg.clientId })}
              >
                <div className="row-main">
                  <div className="row-primary">{pkg.clientName}</div>
                  <div className="row-secondary">
                    {pkg.name}{pkg.isOneOff ? ' · One-off' : ''}
                  </div>
                </div>
                <div className="row-meta">
                  <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>
                    {remaining.toLocaleString()}
                  </span>
                  <ChevronRight size={18} color="var(--muted-strong)" />
                </div>
              </div>
            );
          })}
        </Section>
      )}
    </div>
  );
}
